import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { X, Filter, Search } from "lucide-react";

interface FilterState {
  search: string;
  role: string;
  subscriptionType: string;
  creditsRange: string;
  dateRange: string;
}

interface AdvancedUserFiltersProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  onReset: () => void;
}

export default function AdvancedUserFilters({ filters, onFiltersChange, onReset }: AdvancedUserFiltersProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const updateFilter = (key: keyof FilterState, value: string) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const clearFilter = (key: keyof FilterState) => {
    onFiltersChange({ ...filters, [key]: "" });
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => value !== "").length;
  };

  const hasActiveFilters = getActiveFiltersCount() > 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
            {hasActiveFilters && (
              <Badge variant="secondary">{getActiveFiltersCount()}</Badge>
            )}
          </CardTitle>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              {showAdvanced ? "Simple" : "Advanced"}
            </Button>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={onReset}>
                Clear All
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search by username, email, or name..."
            value={filters.search}
            onChange={(e) => updateFilter("search", e.target.value)}
            className="pl-10"
          />
          {filters.search && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => clearFilter("search")}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Advanced Filters */}
        {showAdvanced && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {/* Role Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Role</label>
              <Select value={filters.role} onValueChange={(value) => updateFilter("role", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All roles</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Subscription Type Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Subscription</label>
              <Select value={filters.subscriptionType} onValueChange={(value) => updateFilter("subscriptionType", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All subscriptions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All subscriptions</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Credits Range Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Credits</label>
              <Select value={filters.creditsRange} onValueChange={(value) => updateFilter("creditsRange", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All credit levels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All credit levels</SelectItem>
                  <SelectItem value="0">No credits</SelectItem>
                  <SelectItem value="1-10">1-10 credits</SelectItem>
                  <SelectItem value="11-50">11-50 credits</SelectItem>
                  <SelectItem value="50+">50+ credits</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date Range Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Joined</label>
              <Select value={filters.dateRange} onValueChange={(value) => updateFilter("dateRange", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">This week</SelectItem>
                  <SelectItem value="month">This month</SelectItem>
                  <SelectItem value="quarter">This quarter</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 pt-2 border-t">
            {filters.search && (
              <Badge variant="secondary" className="flex items-center gap-1">
                Search: {filters.search}
                <X className="h-3 w-3 cursor-pointer" onClick={() => clearFilter("search")} />
              </Badge>
            )}
            {filters.role && (
              <Badge variant="secondary" className="flex items-center gap-1">
                Role: {filters.role}
                <X className="h-3 w-3 cursor-pointer" onClick={() => clearFilter("role")} />
              </Badge>
            )}
            {filters.subscriptionType && (
              <Badge variant="secondary" className="flex items-center gap-1">
                Subscription: {filters.subscriptionType}
                <X className="h-3 w-3 cursor-pointer" onClick={() => clearFilter("subscriptionType")} />
              </Badge>
            )}
            {filters.creditsRange && (
              <Badge variant="secondary" className="flex items-center gap-1">
                Credits: {filters.creditsRange}
                <X className="h-3 w-3 cursor-pointer" onClick={() => clearFilter("creditsRange")} />
              </Badge>
            )}
            {filters.dateRange && (
              <Badge variant="secondary" className="flex items-center gap-1">
                Joined: {filters.dateRange}
                <X className="h-3 w-3 cursor-pointer" onClick={() => clearFilter("dateRange")} />
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}